import { useMemo } from "react";
import cx from "classnames";
import type { Address } from "viem";

import { useTokensData } from "context/SyntheticsStateContext/hooks/globalsHooks";
import { selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { use24hPriceDeltaMap } from "domain/synthetics/tokens/use24hPriceDeltaMap";
import { formatUsdPrice } from "lib/numbers";

/** Cap on distinct symbols, so the strip stays readable on a wide deployment. */
const MAX_SYMBOLS = 8;
/** Repeat the list until the row is wide enough for the -50% loop to be seamless. */
const MIN_CELLS = 8;

type Entry = { symbol: string; address: Address };

/**
 * Market ticker from the design — a marquee of live prices and 24h moves.
 *
 * Symbols are taken from the tokens this deployment actually prices rather than
 * a fixed list, so the strip never advertises a market that isn't there. Prices
 * and deltas use the same helpers and feed as the chart header, so the numbers
 * agree across the page.
 */
export function UsdxTicker() {
  const chainId = useSelector(selectChainId);
  const tokensData = useTokensData();

  const entries = useMemo<Entry[]>(() => {
    if (!tokensData) {
      return [];
    }

    const bySymbol = new Map<string, Entry>();

    for (const token of Object.values(tokensData)) {
      /* Stables sit at $1 and say nothing; wrapped duplicates the native symbol. */
      if (token.isStable || token.isWrapped || !token.prices?.maxPrice) {
        continue;
      }

      if (!token.symbol || bySymbol.has(token.symbol)) {
        continue;
      }

      bySymbol.set(token.symbol, { symbol: token.symbol, address: token.address as Address });
    }

    return [...bySymbol.values()].sort((a, b) => a.symbol.localeCompare(b.symbol)).slice(0, MAX_SYMBOLS);
  }, [tokensData]);

  const deltaMap = use24hPriceDeltaMap(
    chainId,
    useMemo(() => entries.map((e) => e.address), [entries])
  );

  const cells = useMemo(() => {
    if (!tokensData) {
      return [];
    }

    const built = entries
      .map((entry) => {
        const token = tokensData[entry.address];
        const price = formatUsdPrice(token?.prices?.maxPrice, { visualMultiplier: token?.visualMultiplier });

        if (!price || price === "NA") {
          return undefined;
        }

        const delta = deltaMap?.[entry.address]?.deltaPercentage;

        return { symbol: entry.symbol, price, delta };
      })
      .filter((c): c is { symbol: string; price: string; delta: number | undefined } => Boolean(c));

    if (!built.length) {
      return [];
    }

    /* Pad short lists so the marquee row overflows its container. */
    const padded = [...built];
    while (padded.length < MIN_CELLS) {
      padded.push(...built);
    }

    return padded;
  }, [entries, tokensData, deltaMap]);

  if (!cells.length) {
    return null;
  }

  /* Rendered twice so the -50% loop is seamless. */
  const loop = [...cells, ...cells];

  return (
    <div className="overflow-hidden border-b border-slate-600 bg-slate-900 py-8">
      <div className="usdx-marquee flex w-max gap-34">
        {loop.map((cell, index) => (
          <div key={`${cell.symbol}-${index}`} className="flex items-baseline gap-8 whitespace-nowrap text-[12.5px]">
            <span className="font-semibold tracking-[0.4px] text-blue-300">{cell.symbol}</span>
            <span className="text-typography-primary numbers">{cell.price}</span>
            {cell.delta !== undefined && (
              <span className={cx("font-semibold numbers", cell.delta >= 0 ? "text-green-500" : "text-red-500")}>
                {cell.delta >= 0 ? "+" : ""}
                {cell.delta.toFixed(2)}%
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
